import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { SERVER_URL } from "./config";
import "./InvitationResponse.css";

const EVENT_LABELS = {
  wedding: "חתונה",
  bat_mitzva: "בת מצווה",
  bar_mitzva: "בר מצווה",
  hinna: "חינה",
  brit: "ברית",
  brita: "בריתה",
};

const GeneralInvitation = () => {
  const { slug } = useParams();

  const [bride, setBride] = useState("");
  const [groom, setGroom] = useState("");
  const [hall, setHall] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [eventType, setEventType] = useState("wedding");
  const [isSeparated, setIsSeparated] = useState(false);

  const [askKids, setAskKids] = useState(true);
  const [askVegetarian, setAskVegetarian] = useState(true);
  const [kidsAge, setKidsAge] = useState(10);

  const [askDrive, setAskDrive] = useState(false);

  // ✅ shuttle locations from backend: [{id, name}]
  const [shuttleLocations, setShuttleLocations] = useState([]);

  // ✅ drive_choice = "no" OR "<locationId>"
  // IMPORTANT UX rule:
  // - if 0 locations => keep "no"
  // - if 1 location => UI shows only Yes/No, Yes maps to that id
  // - if 2+ locations => UI shows No + Yes from X/Y/Z
  const [driveChoice, setDriveChoice] = useState("no");

  const [guestCount, setGuestCount] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phonePrefix, setPhonePrefix] = useState("050");
  const [phoneRest, setPhoneRest] = useState("");
  const [relationship, setRelationship] = useState("");
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [boysCount, setBoysCount] = useState("");
  const [girlsCount, setGirlsCount] = useState("");

  const [kidsCount, setKidsCount] = useState("");
  const [vegetarianCount, setVegetarianCount] = useState("");

  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const SINGLE_NAME_EVENTS = useMemo(
    () => new Set(["bat_mitzva", "bar_mitzva", "brit", "brita"]),
    []
  );

  useEffect(() => {
    let alive = true;

    axios
      .get(`${SERVER_URL}invitation/event-info/${slug}/`)
      .then((res) => {
        if (!alive) return;
        const data = res.data || {};

        setBride(data.bride || "");
        setGroom(data.groom || "");
        setHall(data.hall || "");
        setDate(data.date || "");
        setTime(data.time || "");
        setEventType(data.event_type || "wedding");
        setIsSeparated(!!data.is_separated_event);

        setAskKids(data.ask_kids ?? true);
        setAskVegetarian(data.ask_vegetarian ?? true);
        setKidsAge(data.kids_age ?? 10);

        const driveEnabled = !!data.ask_drive;
        setAskDrive(driveEnabled);

        // Expect: shuttle_locations: [{id, name}] (name already localized on backend)
        const locs = Array.isArray(data.shuttle_locations)
          ? data.shuttle_locations
          : [];

        const cleaned = locs
          .map((x) => ({
            id: x?.id,
            name: String(x?.name || "").trim(),
          }))
          .filter((x) => x.id != null && x.name);

        setShuttleLocations(cleaned);

        // reset selection on load
        setDriveChoice("no");
      })
      .catch(() => setFormError("שגיאה בטעינת פרטי האירוע."));

    return () => {
      alive = false;
    };
  }, [slug]);

  const subjectName = SINGLE_NAME_EVENTS.has(eventType)
    ? groom || ""
    : [groom, bride].filter(Boolean).join(" ו-");

  const eventHeb = EVENT_LABELS[eventType] || "אירוע";

  const inc = (val, setter, min = 0) => {
    const n = Math.max(min, parseInt(val || 0, 10) + 1);
    setter(String(n));
  };

  const dec = (val, setter, min = 0) => {
    const n = Math.max(min, parseInt(val || 0, 10) - 1);
    setter(String(n));
  };

  // ---------- DRIVE helpers ----------
  const hasDriveLocations = askDrive && shuttleLocations.length > 0;
  const isSingleDriveLocation = hasDriveLocations && shuttleLocations.length === 1;

  const singleLocationId = isSingleDriveLocation
    ? String(shuttleLocations[0].id)
    : null;

  // When only 1 location:
  // - user sees Yes/No
  // - Yes maps to that location id (not the string "yes")
  const setDriveYesSingle = () => {
    if (!singleLocationId) return;
    setDriveChoice(singleLocationId);
  };

  const isDriveNo = String(driveChoice) === "no";
  const isDriveSomeLocation =
    hasDriveLocations &&
    !isDriveNo &&
    shuttleLocations.some((x) => String(x.id) === String(driveChoice));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const fullPhone = `${phonePrefix}${phoneRest}`;

    if (!firstName || !lastName) {
      setFormError("נא למלא שם פרטי ושם משפחה.");
      return;
    }
    if (!phoneRest || phoneRest.length !== 7) {
      setFormError("מספר הטלפון חייב להיות 10 ספרות (3+7).");
      return;
    }
    if (guestCount === "" || Number(guestCount) < 1) {
      setFormError("יש להזין כמה אנשים יגיעו (לפחות 1).");
      return;
    }
    if (!relationship) {
      setFormError("נא לבחור את הקשר שלך לזוג.");
      return;
    }

    const totalGuests = parseInt(guestCount || 0, 10);

    if (isSeparated) {
      const b = parseInt(boysCount || 0, 10);
      const g = parseInt(girlsCount || 0, 10);
      if (b === 0 && g === 0) {
        setFormError("יש למלא לפחות מספר בנים או מספר בנות.");
        return;
      }
      if (b + g !== totalGuests) {
        setFormError("סה״כ בנים + בנות חייב להיות שווה למספר המגיעים.");
        return;
      }
    }

    if (askVegetarian) {
      const v = parseInt(vegetarianCount || 0, 10);
      if (isNaN(v) || v < 0 || v > totalGuests) {
        setFormError("מנות צמחוניות: מספר בין 0 לסה״כ המגיעים.");
        return;
      }
    }
    if (askKids) {
      const k = parseInt(kidsCount || 0, 10);
      if (isNaN(k) || k < 0 || k > totalGuests) {
        setFormError("מספר הילדים: בין 0 לסה״כ המגיעים.");
        return;
      }
    }

    // ✅ validate drive_choice
    // rules:
    // - if askDrive but no locations -> must be "no"
    // - if 1+ locations -> allowed: "no" OR one of the ids in shuttleLocations
    if (askDrive) {
      if (shuttleLocations.length === 0) {
        if (String(driveChoice) !== "no") {
          setFormError("בחירת הסעה לא תקינה.");
          return;
        }
      } else {
        if (!isDriveNo && !isDriveSomeLocation) {
          setFormError("בחירת הסעה לא תקינה.");
          return;
        }
      }
    }

    const payload = {
      name: fullName,
      phone: fullPhone,
      arriving: guestCount,
      note,
      relationship,
    };

    if (isSeparated) {
      payload.boys_count = boysCount || 0;
      payload.girls_count = girlsCount || 0;
    }
    if (askVegetarian) payload.vegetarian_count = vegetarianCount || 0;
    if (askKids) payload.kids_count = kidsCount || 0;

    // ✅ send drive_choice
    if (askDrive) payload.drive_choice = String(driveChoice || "no");

    try {
      setSubmitting(true);
      await axios.post(`${SERVER_URL}invitation/general/${slug}/`, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setFormError("שגיאה בשליחה. נא לנסות שוב מאוחר יותר.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="invitation-container">
        <header className="hero">
          <div className="rings">💍</div>
          <h1>תודה! נרשמתם בהצלחה</h1>
        </header>
        <section className="response-summary">
          <h2>מחכים לראותכם! 💖</h2>
        </section>
      </div>
    );
  }

  return (
    <div className="invitation-container" translate="no">
      <header className="hero">
        <div className="rings">💍</div>
        <h1>
          מוזמנים ל{eventHeb} של {subjectName}
        </h1>
        <p className="sub">
          {hall && <span className="chip">אולם {hall}</span>}
          {date && <span className="chip">{date}</span>}
          {time && <span className="chip">{time}</span>}
        </p>
      </header>

      <form onSubmit={handleSubmit} className="response-form" noValidate>
        <h2>אישור הגעה</h2>

        {formError && (
          <div className="form-error" role="alert" aria-live="assertive">
            {formError}
          </div>
        )}

        {/* שם */}
        <label className="field">
          <span>שם פרטי</span>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </label>

        <label className="field">
          <span>שם משפחה</span>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </label>

        {/* טלפון */}
        <label className="field">
          <span>מספר טלפון</span>
          <div className="phone-row">
            <select
              value={phonePrefix}
              onChange={(e) => setPhonePrefix(e.target.value)}
              required
            >
              <option value="050">050</option>
              <option value="052">052</option>
              <option value="053">053</option>
              <option value="054">054</option>
              <option value="055">055</option>
              <option value="058">058</option>
            </select>
            <input
              type="text"
              value={phoneRest}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 7);
                setPhoneRest(val);
              }}
              placeholder="1234567"
              required
            />
          </div>
        </label>

        {/* כמה תגיעו? */}
        <label className="field">
          <span>כמה תגיעו?</span>
          <div className="stepper">
            <button
              type="button"
              onClick={() => dec(guestCount, setGuestCount, 1)}
              aria-label="הפחת אחד"
            >
              −
            </button>
            <input
              type="number"
              inputMode="numeric"
              min="1"
              value={guestCount}
              onChange={(e) => setGuestCount(e.target.value)}
            />
            <button
              type="button"
              onClick={() => inc(guestCount, setGuestCount, 1)}
              aria-label="הוסף אחד"
            >
              +
            </button>
          </div>
        </label>

        {/* הפרדה מלאה */}
        {isSeparated && (
          <div className="card split-count">
            <p className="hint">האירוע בהפרדה מלאה:</p>
            <div className="grid-2">
              <label className="field">
                <span>כמה בנים?</span>
                <div className="stepper">
                  <button
                    type="button"
                    onClick={() => dec(boysCount, setBoysCount, 0)}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={boysCount}
                    onChange={(e) => setBoysCount(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => inc(boysCount, setBoysCount, 0)}
                  >
                    +
                  </button>
                </div>
              </label>

              <label className="field">
                <span>כמה בנות?</span>
                <div className="stepper">
                  <button
                    type="button"
                    onClick={() => dec(girlsCount, setGirlsCount, 0)}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={girlsCount}
                    onChange={(e) => setGirlsCount(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => inc(girlsCount, setGirlsCount, 0)}
                  >
                    +
                  </button>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* צמחוני */}
        {askVegetarian && (
          <label className="field">
            <span>כמה מנות צמחוניות נדרשות?</span>
            <div className="stepper">
              <button
                type="button"
                onClick={() => dec(vegetarianCount, setVegetarianCount, 0)}
              >
                −
              </button>
              <input
                type="number"
                min="0"
                value={vegetarianCount}
                onChange={(e) => setVegetarianCount(e.target.value)}
              />
              <button
                type="button"
                onClick={() => inc(vegetarianCount, setVegetarianCount, 0)}
              >
                +
              </button>
            </div>
          </label>
        )}

        {/* ילדים */}
        {askKids && (
          <label className="field">
            <span>
              כמה ילדים מגיעים{kidsAge === 0 ? "" : ` (עד גיל ${kidsAge})`}?
            </span>
            <div className="stepper">
              <button
                type="button"
                onClick={() => dec(kidsCount, setKidsCount, 0)}
              >
                −
              </button>
              <input
                type="number"
                min="0"
                value={kidsCount}
                onChange={(e) => setKidsCount(e.target.value)}
              />
              <button
                type="button"
                onClick={() => inc(kidsCount, setKidsCount, 0)}
              >
                +
              </button>
            </div>
          </label>
        )}

        {/* ✅ DRIVE UI: אם יש מיקום אחד -> רק כן/לא. אם יש כמה -> לא + כן מX/Y/Z */}
        {askDrive && shuttleLocations.length > 0 && (
          <label className="field">
            <span>יש הסעה — האם תגיעו בהסעה?</span>

            <div
              className="drive-choice"
              role="radiogroup"
              aria-label="בחירת הסעה"
            >
              {/* לא */}
              <button
                type="button"
                className={`choice-btn ${
                  String(driveChoice) === "no" ? "active" : ""
                }`}
                onClick={() => setDriveChoice("no")}
                aria-pressed={String(driveChoice) === "no"}
              >
                לא
              </button>

              {/* ✅ מיקום אחד: כן */}
              {shuttleLocations.length === 1 ? (
                <button
                  type="button"
                  className={`choice-btn ${
                    String(driveChoice) === singleLocationId ? "active" : ""
                  }`}
                  onClick={setDriveYesSingle}
                  aria-pressed={String(driveChoice) === singleLocationId}
                >
                  כן
                </button>
              ) : (
                /* ✅ כמה מיקומים: כן מX/Y/Z */
                shuttleLocations.map((loc) => {
                  const idStr = String(loc.id);
                  return (
                    <button
                      key={idStr}
                      type="button"
                      className={`choice-btn ${
                        String(driveChoice) === idStr ? "active" : ""
                      }`}
                      onClick={() => setDriveChoice(idStr)}
                      aria-pressed={String(driveChoice) === idStr}
                    >
                      כן מ{loc.name}
                    </button>
                  );
                })
              )}
            </div>

            {/* הסבר קטן רק כשיש כמה מקומות */}
            {shuttleLocations.length > 1 && (
              <small style={{ opacity: 0.8, display: "block", marginTop: 6 }}>
                בחרו מאיפה תרצו לעלות להסעה.
              </small>
            )}
          </label>
        )}

        {/* fallback: askDrive=true אבל אין מיקומים */}
        {askDrive && shuttleLocations.length === 0 && (
          <div className="card" style={{ marginTop: 10 }}>
            <p style={{ margin: 0 }}>
              שימי לב: האירוע מוגדר עם "הסעה", אבל לא התקבלה רשימת מקומות מהשרת.
              צריך שה-API יחזיר shuttle_locations.
            </p>
          </div>
        )}

        {/* קשר */}
        <label className="field">
          <span>מה הקשר שלך לזוג?</span>
          <select
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            required
          >
            <option value="">בחר קשר</option>
            <option value="חברים חתן">חברים חתן</option>
            <option value="חברים כלה">חברים כלה</option>
            <option value="חברים הורים">חברים הורים</option>
            <option value="משפחה חתן">משפחה חתן</option>
            <option value="משפחה כלה">משפחה כלה</option>
            <option value="אחר">אחר</option>
          </select>
        </label>

        {/* הערה */}
        <label className="field">
          <span>הערות (עד 30 תווים)</span>
          <textarea
            value={note}
            onChange={(e) => {
              if (e.target.value.length <= 30) setNote(e.target.value);
            }}
            maxLength={30}
            placeholder="כתבו כאן…"
          />
          <div className="char-counter">{note.length}/30</div>
        </label>

        <div className="sticky-actions">
          <button
            className="btn primary wide"
            type="submit"
            disabled={submitting}
          >
            {submitting ? "שולח…" : "שלחו תשובה"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GeneralInvitation;
